import Head from 'next/head'
import Script from 'next/script'
import { googleAnalyticsGTag, mainLinkPreviewImage } from '@/config'

import { slugifyWithCounter } from '@sindresorhus/slugify'
import Header from '@/components/Header'
import { IntercomProvider } from '@/utils/intercom'

import { DocsLayout } from '@/components/DocsLayout'
import { BlogLayout } from '@/components/BlogLayout'
import { PageLayout } from '@/components/PageLayout'
import Notification from '@/components/Notification'
import { setUseWhatChange } from '@simbathesailor/use-what-changed'

import { Inter } from 'next/font/google'

import 'focus-visible'
import '@/styles/tailwind.css'

import { heroDescription, heroTagline, isDev } from '@/config'

import { NotificationProvider } from '@/utils/notifications'
import { Footer } from '@/components/Footer'

setUseWhatChange(isDev)

const inter = Inter({
  subsets: ['latin'],
  weight: ['300', '400', '500', '700'],
  display: 'swap',
})

// const roboto = Roboto({
//   subsets: ['latin'],
//   weight: ['300', '400', '500', '700'],
//   display: 'swap',
// })

// const robotoMono = Roboto_Mono({
//   subsets: ['latin'],
//   weight: ['400', '500', '700'],
//   display: 'swap',
// })

const isProduction = process.env.NODE_ENV === 'production'

function getNodeText(node) {
  let text = ''
  for (const child of node.children ?? []) {
    if (typeof child === 'string') {
      text += child
    }
    text += getNodeText(child)
  }
  return text
}

function collectHeadings(nodes, slugify = slugifyWithCounter()) {
  const sections = []

  for (const node of nodes) {
    if (node.name === 'h2' || node.name === 'h3') {
      const title = getNodeText(node)
      if (title) {
        const id = slugify(title)
        node.attributes.id = id
        if (node.name === 'h3') {
          if (!sections[sections.length - 1]) {
            throw new Error(
              `Cannot add h3 ${title} to table of contents without a preceding h2`
            )
          }
          sections[sections.length - 1].children.push({
            ...node.attributes,
            title,
          })
        } else {
          sections.push({ ...node.attributes, title, children: [] })
        }
      }
    }

    sections.push(...collectHeadings(node.children ?? [], slugify))
  }

  return sections
}

function HeadComponent({
  title,
  description,
  markdocMeta,
  router,
  enableGA = true,
}) {
  return (
    <>
      <Head>
        <title>{title}</title>
        <meta name="description" content={description} />
        <meta name="twitter:site" content="@select_dev" />
        <meta name="twitter:creator" content="@select_dev" />
        <meta name="twitter:title" content={title} />
        <meta name="twitter:description" content={description} />
        <meta name="twitter:card" content="summary_large_image" />

        {markdocMeta?.image ? (
          <>
            <meta
              property="og:image"
              content={`https://select.dev/cdn-cgi/imagedelivery/1zmOcgV1p520E4lLTrYjjg/${markdocMeta.image}/public`}
            />
            <meta
              name="twitter:image"
              content={`https://select.dev/cdn-cgi/imagedelivery/1zmOcgV1p520E4lLTrYjjg/${markdocMeta.image}/public`}
            />
          </>
        ) : (
          <>
            <meta property="og:image" content={mainLinkPreviewImage} />
            <meta name="twitter:image" content={mainLinkPreviewImage} />
          </>
        )}
        {markdocMeta ? (
          <meta property="og:type" content="article" />
        ) : (
          <meta property="og:type" content="website" />
        )}
        <meta
          property="og:url"
          content={`https://select.dev${router.pathname}`}
        />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={description} />
      </Head>

      {enableGA && (
        <>
          <Script
            src={`https://www.googletagmanager.com/gtag/js?id=${googleAnalyticsGTag}`}
            strategy="afterInteractive"
          />
          <Script id="google-analytics" strategy="afterInteractive">
            {`
              window.dataLayer = window.dataLayer || [];
              function gtag(){dataLayer.push(arguments);}
              gtag('js', new Date());
              gtag('config', '${googleAnalyticsGTag}', {
                page_path: window.location.pathname,
              });
            `}
          </Script>
        </>
      )}

      {isProduction && (
        <>
          <Script id="ms_clarity" strategy="afterInteractive">
            {`
            (function(c,l,a,r,i,t,y){
                c[a]=c[a]||function(){(c[a].q=c[a].q||[]).push(arguments)};
                t=l.createElement(r);t.async=1;t.src="https://www.clarity.ms/tag/"+i;
                y=l.getElementsByTagName(r)[0];y.parentNode.insertBefore(t,y);
            })(window, document, "clarity", "script", "qgxtrf5e5z");V
          `}
          </Script>

          <Script
            id="warmly-script-loader"
            src="https://opps-widget.getwarmly.com/warmly.js?clientId=7d8e79b4722ddb6a4f8e6097b79dc27d"
            strategy="afterInteractive"
            defer
          />

          <Script id="factors-ai" strategy="afterInteractive">
            {`window.faitracker=window.faitracker||function(){this.q=[];var t=new CustomEvent("FAITRACKER_QUEUED_EVENT");return this.init=function(t,e,a){this.TOKEN=t,this.INIT_PARAMS=e,this.INIT_CALLBACK=a,window.dispatchEvent(new CustomEvent("FAITRACKER_INIT_EVENT"))},this.call=function(){var e={k:"",a:[]};if(arguments&&arguments.length>=1){for(var a=1;a<arguments.length;a++)e.a.push(arguments[a]);e.k=arguments[0]}this.q.push(e),window.dispatchEvent(t)},this.message=function(){window.addEventListener("message",function(t){"faitracker"===t.data.origin&&this.call("message",t.data.type,t.data.message)})},this.message(),this.init("zupwskfxq6w1bwm0561xy9yfg2xt74r2",{host:"https://api.factors.ai"}),this}(),function(){var t=document.createElement("script");t.type="text/javascript",t.src="https://app.factors.ai/assets/factors.js",t.async=!0,(d=document.getElementsByTagName("script")[0]).parentNode.insertBefore(t,d)}();`}
          </Script>

          <Script id="snitcher" strategy="afterInteractive">
            {`!function(s,n,i,t,c,h){s.SnitchObject=i;s[i]||(s[i]=function(){
            (s[i].q=s[i].q||[]).push(arguments)});s[i].l=+new Date;c=n.createElement(t);
            h=n.getElementsByTagName(t)[0];c.src='//snid.snitcher.com/8430862.js';
            h.parentNode.insertBefore(c,h)}(window,document,'snid','script');
                
            snid('verify', '8430862');`}
          </Script>
        </>
      )}
    </>
  )
}

export default function App({ Component, pageProps, router }) {
  const pageTitle =
    pageProps.markdoc?.frontmatter.pageTitle ||
    `${pageProps.markdoc?.frontmatter.title}`

  const description = pageProps.markdoc?.frontmatter.description
  const markdocMeta = pageProps.markdoc?.frontmatter
  const hideTableOfContents =
    pageProps.markdoc?.frontmatter?.hideTableOfContents ?? false

  const tableOfContents =
    pageProps.markdoc?.content && !hideTableOfContents
      ? collectHeadings(pageProps.markdoc.content)
      : []
  const isMarkdoc = Object.hasOwn(pageProps, 'markdoc')
  const isChangelog = router.asPath.startsWith('/changelog')
  const isBlog = router.asPath.startsWith('/posts')
  const isDocs = router.asPath.startsWith('/docs')

  if (isMarkdoc) {
    let title
    let LayoutComponent

    if (isBlog || isChangelog) {
      title = `${pageTitle}`
      LayoutComponent = BlogLayout
    } else if (isDocs) {
      title = `${pageTitle}`
      LayoutComponent = DocsLayout
    } else {
      title = `${pageTitle} | SELECT`
      LayoutComponent = PageLayout
    }
    return (
      <div className={`${inter.className} mt-[132px]`}>
        <Header />
        <IntercomProvider isApp={false} />
        <HeadComponent
          title={title}
          description={description}
          markdocMeta={markdocMeta}
          router={router}
        />
        <LayoutComponent
          title={pageTitle}
          tableOfContents={tableOfContents}
          blogMeta={markdocMeta}
          isChangelog={isChangelog}
        >
          <Component {...pageProps} />
        </LayoutComponent>
        <Footer />
      </div>
    )
  }

  return (
    <div className={`${inter.className}`}>
      <NotificationProvider>
        <Header />
        <IntercomProvider isApp={false} />
        <HeadComponent
          title={`SELECT | ${heroTagline}`}
          description={heroDescription}
          router={router}
        />
        <div className="flex min-h-[calc(100vh-4rem)] flex-col">
          <Component {...pageProps} />
        </div>
        <Notification />
        <Footer />
      </NotificationProvider>
    </div>
  )
}
